import { useState } from 'react';
import { Layout } from 'antd';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { Badge } from 'components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/ui/dialog';
import useQueryParams from '../../hooks/useQueryParams';
import HeaderBar from '../../Layout/HeaderBar';
import FooterBar from '../../Layout/FooterBar';
import { IOrg } from '../../modules/orgs/interfaces';
import TableList from './Table';
import AddOrg from './AddOrg';
import OrgStats from './OrgStats';
import AdminOnly from '../../components/AdminOnly';
import { useTranslation } from 'react-i18next';
import { DBGroupShape } from '../../modules/groups/types';
import { Button } from 'components/ui/button';

const { Content } = Layout;

export function DeleteCompany({ title, deleteOrg }: { title: string; deleteOrg: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-destructive"
          aria-label={`${t('Slet afdeling')}: ${title}`}
        >
          <Trash2 aria-hidden="true" />
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[calc(100%-2rem)] rounded-xl">
        <DialogHeader>
          <DialogTitle>{t('home.org.delete.title')}</DialogTitle>
          <DialogDescription>{title}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" autoFocus onClick={() => setOpen(false)}>
            {t('home.org.delete.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              deleteOrg();
              setOpen(false);
            }}
          >
            {t('home.org.delete.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface IHomeViewProps {
  orgs: IOrg[];
  groups: DBGroupShape[];
  deleteOrg: (orgId: string) => void;
}

export type GroupedOrgs = IOrg | { id: string; subGroup: string; title: string; orgs: IOrg[] };

export function groupOrgsBySubGroup(orgs: IOrg[]): GroupedOrgs[] {
  const grouped = new Map<string, { title: string; orgs: IOrg[] }>();

  // Group orgs by subGroup ID
  for (const org of orgs) {
    if (org.subGroup) {
      const key = org.subGroup.id;
      if (!grouped.has(key)) {
        grouped.set(key, { title: org.subGroup.title, orgs: [] });
      }
      grouped.get(key)!.orgs.push(org);
    }
  }

  const result: GroupedOrgs[] = [];

  // Add grouped organizations to result using keys from the map
  grouped.forEach((data, subGroup) => {
    result.push({ id: subGroup, subGroup, title: data.title, orgs: data.orgs });
  });

  // Add ungrouped organizations to result
  orgs.filter((org) => !org.subGroup).forEach((org) => result.push(org));

  return result.sort((a, b) => {
    if (a.title && b.title) {
      return a.title.localeCompare(b.title, 'da', {
        sensitivity: 'base',
        numeric: true,
      });
    }
    return 0;
  });
}

function HomeView({ orgs, groups, deleteOrg }: IHomeViewProps) {
  const { t } = useTranslation();
  const groupId = useQueryParams().get('groupId');
  const group = groups.find((group) => group.id === groupId);
  const grouped = groupOrgsBySubGroup(orgs);

  return (
    <Layout>
      <HeaderBar />
      <Content className="px-4 py-6 md:px-6 md:py-8">
        <div className="mx-auto max-w-7xl space-y-6">
          {groupId && (
            <Button asChild variant="ghost" size="sm" className="-ml-3">
              <Link to="/groups">
                <ArrowLeft aria-hidden="true" />
                {t('Tilbage til grupper')}
              </Link>
            </Button>
          )}
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="m-0 text-2xl font-semibold tracking-tight text-foreground">
                  {t('header.depts')}
                </h1>
                <Badge variant="secondary">{orgs.length}</Badge>
              </div>
              <p className="mb-0 mt-2 text-sm text-muted-foreground">
                {group ? group.title : t('Overblik over dine afdelinger, undergrupper og adgange.')}
              </p>
            </div>
            <AddOrg />
          </div>
          <AdminOnly>
            <OrgStats />
          </AdminOnly>
          <TableList deleteOrg={deleteOrg} data={grouped} />
        </div>
      </Content>
      <FooterBar />
    </Layout>
  );
}

export default HomeView;
